// Updated to use real Firebase auth from transition-web
import React from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { SignInState, SignInSteps, SignInViewProps } from '../types/SignInTypes'
import { useTranslation } from '../shared/hooks/useIntractionTranslate'
import useAppState from '../hooks/useAppState'
import useLogin from '../hooks/useLogin'
import useSignInState from '../hooks/useSignInState'
import { DEFAULT_SIGN_IN_STATE } from '../libs/SignInHelper'
import * as SignInMachineHelper from '../libs/SignInMachine'
import onUnhandledPromiseRejection from '../libs/onUnhandledPromiseRejection'
import { onLoginToIntraction } from '../libs/onLoginToIntraction'
// import { Button } from '../interaxDesignSystemComponents'
import SignInLanding from './SignInLanding'
import SignInEnterCode from './SignInEnterCode'

export default function SignIn() {
  const { state } = useAppState()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = React.useState(false)
  const [serverError, setServerError] = React.useState('')
  const { signInState, setSignInState } = useSignInState()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Onboarding/invite deep-link params: when the recipient lands on /login via
  // an org invite redirect, prefill + (optionally) lock the email and round-trip
  // the invite token back through /invite/<token> after a successful sign-in.
  const invitePrefillEmail = searchParams.get('email') || ''
  const inviteToken = searchParams.get('inviteToken') || ''
  const lockEmail = searchParams.get('lockEmail') === '1'
  const inviteOrgName = searchParams.get('orgName') || ''
  const inviteIsOnboarding = searchParams.get('isOnboarding') === '1'
  const inviteMode = Boolean(inviteToken)

  const buildInviteReturnPath = React.useCallback(() => {
    const params = new URLSearchParams()
    if (inviteIsOnboarding) params.set('isOnboarding', '1')
    if (inviteOrgName) params.set('orgName', inviteOrgName)
    const query = params.toString()
    return query ? `/invite/${inviteToken}?${query}` : `/invite/${inviteToken}`
  }, [inviteToken, inviteIsOnboarding, inviteOrgName])

  // Locked invite flow: keep re-applying the invited email if a later step clears it
  // (was going blank in sign-up). Unlocked flow: prefill once so the user can edit.
  const hasAppliedPrefillRef = React.useRef(false)
  React.useEffect(() => {
    if (!invitePrefillEmail) return
    if (lockEmail) {
      if (signInState.data.email !== invitePrefillEmail) {
        setSignInState((prev) => ({
          ...prev,
          data: { ...prev.data, email: invitePrefillEmail }
        }))
      }
      return
    }
    if (hasAppliedPrefillRef.current) return
    hasAppliedPrefillRef.current = true
    setSignInState((prev) => ({
      ...prev,
      data: { ...prev.data, email: invitePrefillEmail }
    }))
  }, [invitePrefillEmail, lockEmail, signInState.data.email, setSignInState])

  const loading = useLogin({
    onLogin: (state) => {
      // If the user arrived via an org invite, send them back to the invite
      // acceptor instead of the default role-based landing route.
      if (inviteToken) {
        navigate(buildInviteReturnPath(), { replace: true })
        return Promise.resolve()
      }

      return onLoginToIntraction(state, { navigate, window, locationState: location.state })
    },
    onFinally: () => {
      setSignInState(DEFAULT_SIGN_IN_STATE)
      setIsLoading(false)
    }
  })

  // If user is already authenticated, redirect to signed in page
  React.useEffect(() => {
    if (state.user.id) {
      // Honor the invite redirect when an already-authenticated user clicks the
      // invite link (their email match is still verified by AcceptOrgInvitePage).
      if (inviteToken) {
        navigate(buildInviteReturnPath(), { replace: true })
        return
      }
      navigate('/signedin')
    }
  }, [state.user.id, navigate, inviteToken, buildInviteReturnPath])

  const onError = (err: unknown) => {
    // Invitation-only login refusal: stay on the email form and show a helpful message
    // instead of resetting the flow (the backend sends the machine code 'not-invited').
    if (err instanceof Error && err.message.includes('not-invited')) {
      setServerError(t('auth.notInvited'))
      return
    }
    if (signInState.step === SignInSteps.ENTER_CODE) throw err
    onUnhandledPromiseRejection(err)
    setSignInState(DEFAULT_SIGN_IN_STATE)
  }

  const signInViewProps: SignInViewProps = {
    isLoading: isLoading || loading,
    signInState: signInState,
    disableEditEmail: lockEmail,
    inviteMode,
    inviteOrganizationName: inviteOrgName,
    serverError,
    onClickBack: () => {
      switch (signInState.step) {
        default:
          return setSignInState(DEFAULT_SIGN_IN_STATE)
      }
    },
    onTakeMeBack: () => setSignInState(DEFAULT_SIGN_IN_STATE),
    onPressContinue: (signInState) =>
      Promise.resolve(setIsLoading(true))
        .then(() => setServerError(''))
        .then(() => SignInMachineHelper.onPressContinue(signInState, state, { navigate }))
        .then((signInState: SignInState) => {
          setSignInState(signInState)
        })
        .catch(onError)
        .finally(() => setIsLoading(false)),
    onLoginWithGoogle: () => {
      // TODO: Implement Google login
    },
    onLoginWithMicrosoft: () => {
      // TODO: Implement Microsoft login
    },
    onLoginWithApple: () => {
      // TODO: Implement Apple login
    }
  }

  // Render different components based on sign-in step

  switch (signInState.step) {
    case SignInSteps.LANDING:
      return <SignInLanding {...signInViewProps} />
    case SignInSteps.ENTER_CODE:
      return <SignInEnterCode {...signInViewProps} />
    default:
      return <SignInLanding {...signInViewProps} />
  }
}
